"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'

type TariffRateData = {
  id: string
  name: string
  line_category: string
  rate_per_ton_km: number
  currency: string
  valid_from: string
  valid_to: string | null
  provider_type: string
  infrastructure_manager: string | null
  min_charge: number | null
  is_active: boolean
}

type ApiResponse = {
  items: Array<Record<string, unknown>>
  total: number
}

const LINE_CATEGORY_OPTIONS = [
  { value: 'TRUNK', label: 'Trunk' },
  { value: 'MAIN', label: 'Main' },
  { value: 'SECONDARY', label: 'Secondary' },
  { value: 'LOCAL', label: 'Local' },
]

const PROVIDER_TYPE_OPTIONS = [
  { value: 'PLK', label: 'PLK (National)' },
  { value: 'PRIVATE', label: 'Private' },
]

function mapData(item: Record<string, unknown>): TariffRateData | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    name: typeof item.name === 'string' ? item.name : '',
    line_category: typeof item.line_category === 'string' ? item.line_category : '',
    rate_per_ton_km: typeof item.rate_per_ton_km === 'number' ? item.rate_per_ton_km : 0,
    currency: typeof item.currency === 'string' ? item.currency : 'PLN',
    valid_from: typeof item.valid_from === 'string' ? item.valid_from : '',
    valid_to: typeof item.valid_to === 'string' ? item.valid_to : null,
    provider_type: typeof item.provider_type === 'string' ? item.provider_type : '',
    infrastructure_manager: typeof item.infrastructure_manager === 'string' ? item.infrastructure_manager : null,
    min_charge: typeof item.min_charge === 'number' ? item.min_charge : null,
    is_active: typeof item.is_active === 'boolean' ? item.is_active : true,
  }
}

export default function TariffRateDetailPage({ params }: { params?: { id?: string } }) {
  const id = typeof params?.id === 'string' ? params.id : ''
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const [data, setData] = React.useState<TariffRateData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!id) {
      setData(null)
      setIsLoading(false)
      setLoadError(t('tariff_rates.error.notFound', 'Tariff rate not found'))
      return
    }
    let cancelled = false

    async function loadData() {
      setIsLoading(true)
      setLoadError(null)
      try {
        const fallback: ApiResponse = { items: [], total: 0 }
        const call = await apiCall<ApiResponse>(
          `/api/tariff_rates/tariff-rates?ids=${encodeURIComponent(id)}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('tariff_rates.error.load', 'Failed to load tariff rate')
          if (!cancelled) setLoadError(message)
          return
        }
        const payload = call.result ?? fallback
        const items = Array.isArray(payload.items) ? payload.items : []
        const first = items[0]
        if (!cancelled) {
          setData(first ? mapData(first) : null)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('tariff_rates.error.load', 'Failed to load tariff rate')
          setLoadError(message)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadData()
    return () => { cancelled = true }
  }, [id, scopeVersion, t])

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'name',
      label: t('tariff_rates.fields.name', 'Name'),
      type: 'text',
      required: true,
    },
    {
      id: 'lineCategory',
      label: t('tariff_rates.fields.lineCategory', 'Line Category'),
      type: 'select',
      required: true,
      options: LINE_CATEGORY_OPTIONS,
    },
    {
      id: 'ratePerTonKm',
      label: t('tariff_rates.fields.ratePerTonKm', 'Rate per ton-km'),
      type: 'number',
      required: true,
    },
    {
      id: 'currency',
      label: t('tariff_rates.fields.currency', 'Currency'),
      type: 'text',
    },
    {
      id: 'validFrom',
      label: t('tariff_rates.fields.validFrom', 'Valid From'),
      type: 'date',
      required: true,
    },
    {
      id: 'validTo',
      label: t('tariff_rates.fields.validTo', 'Valid To'),
      type: 'date',
    },
    {
      id: 'providerType',
      label: t('tariff_rates.fields.providerType', 'Provider Type'),
      type: 'select',
      required: true,
      options: PROVIDER_TYPE_OPTIONS,
    },
    {
      id: 'infrastructureManager',
      label: t('tariff_rates.fields.infrastructureManager', 'Infrastructure Manager'),
      type: 'text',
    },
    {
      id: 'minCharge',
      label: t('tariff_rates.fields.minCharge', 'Minimum Charge'),
      type: 'number',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'tariff',
      title: t('tariff_rates.groups.tariff', 'Tariff Details'),
      fields: ['name', 'lineCategory', 'providerType', 'infrastructureManager'],
    },
    {
      id: 'pricing',
      title: t('tariff_rates.groups.pricing', 'Pricing'),
      fields: ['ratePerTonKm', 'currency', 'minCharge'],
    },
    {
      id: 'validity',
      title: t('tariff_rates.groups.validity', 'Validity Period'),
      fields: ['validFrom', 'validTo'],
    },
  ], [t])

  const initialValues = React.useMemo(() => {
    if (!data) return undefined
    return {
      name: data.name,
      lineCategory: data.line_category,
      ratePerTonKm: data.rate_per_ton_km,
      currency: data.currency,
      validFrom: data.valid_from,
      validTo: data.valid_to ?? undefined,
      providerType: data.provider_type,
      infrastructureManager: data.infrastructure_manager ?? undefined,
      minCharge: data.min_charge ?? undefined,
    }
  }, [data])

  const handleDelete = React.useCallback(async () => {
    if (!data) return
    const confirmed = await confirm({
      title: t('tariff_rates.deleteConfirm', 'Delete tariff rate "{{name}}"?', { name: data.name }),
      variant: 'destructive',
    })
    if (!confirmed) return
    await deleteCrud('tariff_rates/tariff-rates', data.id)
    flash(t('tariff_rates.deleteSuccess', 'Tariff rate deleted'), 'success')
    router.push('/backend/route_planning')
  }, [data, confirm, t, router])

  return (
    <Page>
      <PageBody>
        {isLoading && <LoadingMessage label="" />}
        {loadError && <ErrorMessage label={loadError} />}
        {!isLoading && !loadError && !data && (
          <ErrorMessage label={t('tariff_rates.error.notFound', 'Tariff rate not found')} />
        )}
        {!isLoading && !loadError && data && initialValues && (
          <CrudForm
            title={data.name}
            backHref="/backend/route_planning"
            fields={fields}
            groups={groups}
            initialValues={initialValues}
            submitLabel={t('tariff_rates.update.submit', 'Save Changes')}
            cancelHref="/backend/route_planning"
            onDelete={handleDelete}
            onSubmit={async (values) => {
              await updateCrud('tariff_rates/tariff-rates', {
                id: data.id,
                ...values,
              })
              flash(t('tariff_rates.flash.updated', 'Tariff rate updated'), 'success')
              router.push('/backend/route_planning')
            }}
          />
        )}
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
